import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModel from "../../../components/common/ConfirmationModel";
import {
  DatePicker,
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectWithLabel,
} from "../../../components/micro/input";
import Breadcrumb from "../../../components/common/BreadCrumb";
import { InputError } from "../../../components/error/error";
import { Plus } from "../../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { companiesStaff, createOutlet } from "../../../redux/companies/slice";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  ClearIcon,
  LocalizationProvider,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { IconButton, Stack } from "@mui/material";
import { getProductList } from "../../../redux/product/slice";
import moment from "moment";

const AddNewOutlet = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditMode = location?.state?.mode;
  const outletData = location?.state?.outlet;
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  dayjs.extend(customParseFormat);

  // Selector
  const { rolesList, productsList } = useSelector((store) => ({
    rolesList: store?.companiesData?.companiesStaffStatus?.data?.data,
    productsList: store?.productsData?.getProductListStatus?.data?.data,
  }));

  const handleMenuOpen = (role) => {
    dispatch(
      companiesStaff({
        roles: role,
        pageLimit: "all",
        types: "Outlet",
        outlet: "",
        search: "",
        company_id: companyData?.id || outletData?.company_id || "",
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  };

  useEffect(() => {
    handleMenuOpen(selectedRole);
    dispatch(
      getProductList({
        pageLimit: "all",
        search: "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
  }, [selectedManager, selectedStaff]);

  useEffect(() => {
    if (Array.isArray(rolesList)) {
      const data = rolesList?.map((e) => ({
        label: e?.user?.full_name,
        value: e?.id,
        role: e?.user?.role,
      }));
      setManagerOptions(data?.filter((e) => e.role === "manager"));
      setStaffOptions(data?.filter((e) => e.role === "staff"));
    } else {
      setManagerOptions([]);
      setStaffOptions([]);
    }
  }, [rolesList]);

  const addTimeRange = (e, setFieldValue, values) => {
    e.preventDefault();

    if (values?.avoid_time?.length < 3) {
      const newTimeRange = { startTime: "", endTime: "" };
      setFieldValue("avoid_time", [...values?.avoid_time, newTimeRange]);
    }
  };

  const handleEditClick = () => setIsEditing(true);

  const handleAddClick = () => {
    setShowConfirmationModal(true);
  };

  const handleSubmit = async (values) => {
    const payload = {
      company_id: outletData?.company_id || companyData?.id,
      name: values.outletName,
      address: values.address,
      postal_code: values.postalCode,
      start_time: values.startTime,
      end_time: values.endTime,
      avoid_time: values.avoid_time.map((range) => ({
        id: outletData?.id ? range.id : "",
        start_time: range.startTime,
        end_time: range.endTime,
      })),
      outlet_staff: [
        {
          outlet_manager_id: values?.manager?.value || values?.manager,
          outlet_staff_id: values?.staff?.value || values?.staff,
        },
      ],
      equipment: values.equipments.map((equipment) => ({
        id: outletData?.id ? equipment.id : "",
        on_site_name: equipment.on_site_name,
        product_id: equipment.product_id,
        purchase_date: dayjs(equipment.purchase_date).format("DD-MM-YYYY"),
        include_warranty: equipment.warranty ? 1 : 0,
        warranty_period: equipment.warrantyPeriod || null,
        serial_number: equipment.serialNumber || null,
      })),
      remarks: values.remarks,
    };

    const action = outletData?.id
      ? createOutlet({ id: outletData.id, ...payload })
      : createOutlet(payload);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createOutlet/fulfilled") {
        navigate(-1);
      }
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const productOptions = Array.isArray(productsList?.data || productsList)
    ? (productsList?.data || productsList)
        .filter((product) => product.is_active === 1)
        .map((product) => ({
          value: product.id,
          label: product.name,
        }))
    : [];

  const manager = outletData?.outlet_staff?.find(
    (staff) => staff.user?.role === "manager"
  );

  const staff = outletData?.outlet_staff?.find(
    (staff) => staff.user?.role === "staff"
  );

  return (
    <div>
      <div className="mb-5">
        <h6 className="font-Montserrat font-normal text-xl mb-3">
          Company & Outlets
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Add Outlet"}
          breadCrumbParent2={"Company & Outlets"}
          breadCrumbTitleKey={"/"}
        />
      </div>
      <div>
        <Formik
          initialValues={{
            outletName: outletData?.name || "",
            address: outletData?.address || "",
            postalCode: outletData?.postal_code || "",
            startTime: outletData?.start_time || "09:00:00",
            endTime: outletData?.end_time || "16:00:00",
            avoid_time: outletData?.avoid_times?.map((time) => ({
              id: time?.id || "",
              startTime: time?.start_time
                ? dayjs(time.start_time, "hh:mm A").format("HH:mm:ss")
                : "",
              endTime: time?.end_time
                ? dayjs(time.end_time, "hh:mm A").format("HH:mm:ss")
                : "",
            })) || [{ startTime: "", endTime: "" }],
            manager: manager
              ? {
                  label: manager.user.full_name,
                  value: manager.user.id,
                }
              : null,
            staff: staff
              ? {
                  label: staff.user.full_name,
                  value: staff.user.id,
                }
              : null,
            equipments: outletData?.outlet_equipment?.map((equipment) => ({
              id: equipment?.id || "",
              product_id: equipment?.product_id || "",
              on_site_name: equipment.on_site_name || "",
              purchase_date: moment(equipment.purchase_date),
              warranty: equipment.include_warranty === 1,
              warrantyPeriod: equipment.warranty_period || "",
              serialNumber: equipment.serial_number || "",
            })) || [
              {
                product_id: outletData ? outletData.product_id : "",
                on_site_name: outletData ? outletData.on_site_name : "",
                purchase_date: outletData ? outletData.purchase_date : dayjs(),
                warranty: outletData ? outletData.include_warranty : false,
                warrantyPeriod: outletData ? outletData.warranty_period : "",
                serialNumber: outletData ? outletData.serial_number : "",
              },
            ],
            remarks: outletData?.remarks || "",
          }}
          validationSchema={Yup.object({
            outletName: Yup.string()
              .trim()
              .required("Please enter outlet name"),
            address: Yup.string().trim().required("Please enter address"),
            postalCode: Yup.string()
              .trim()
              .required("Please enter postal code")
              .length(6, "Postal code must be exactly 6 digits")
              .matches(/^\d+$/, "Postal code must be numeric"),
            startTime: Yup.string().required("Please enter start time"),
            endTime: Yup.string().required("Please enter end time"),
            avoid_time: Yup.array()
              .of(
                Yup.object().shape({
                  startTime: Yup.string().required("Please enter start time"),
                  endTime: Yup.string().required("Please enter end time"),
                })
              )
              .max(3, "You can only add up to 3 time ranges"),
            equipments: Yup.array().of(
              Yup.object({
                on_site_name: Yup.string()
                  .trim()
                  .required("Please enter on-site name"),
              })
            ),
          })}
          onSubmit={(values, { setSubmitting }) => {
            setFormValues(values);
            handleAddClick();
            setSubmitting(false);
          }}
        >
          {({
            errors,
            touched,
            handleBlur,
            handleChange,
            values,
            setFieldValue,
          }) => (
            <Form>
              {/* New Outlet */}
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h1 className="text-sm font-normal mb-2">
                  {companyData?.name}
                </h1>
                <h2 className="text-2xl font-normal mb-4 font-Montserrat">
                  {isEditMode === "edit" ? "Edit Outlet" : "Add New Outlet"}
                </h2>
                <h3 className="text-xs font-normal mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.outletName && touched.outletName
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="outletName"
                    label="Outlet Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.outletName}
                    errors={errors.outletName && touched.outletName}
                    errorsText={errors.outletName}
                    disabled={isEditMode == "edit" && !isEditing}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.postalCode && touched.postalCode
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="postalCode"
                    label="Postal Code*"
                    type="number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.postalCode}
                    errors={errors.postalCode && touched.postalCode}
                    errorsText={errors.postalCode}
                    disabled={isEditMode == "edit" && !isEditing}
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                  />
                  <InputWithLabel
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.address && touched.address
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    idFromName="address"
                    label="Address*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    errors={errors.address && touched.address}
                    errorsText={errors.address}
                    disabled={isEditMode == "edit" && !isEditing}
                  />

                  {/* Start Time */}
                  <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Stack spacing={3}>
                        <MobileTimePicker
                          label="Start Time"
                          value={
                            values.startTime
                              ? dayjs(values.startTime, "HH:mm:ss")
                              : dayjs("09:00:00", "HH:mm:ss")
                          }
                          onChange={(newValue) => {
                            const formattedTime =
                              dayjs(newValue).format("HH:mm:ss");
                            setFieldValue("startTime", formattedTime);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                padding: "0px",
                                minHeight: "unset",
                                "& .MuiOutlinedInput-root": {
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#F15B29",
                                    borderWidth: "1px",
                                  },
                                },
                              },
                            },
                          }}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        {errors.startTime && touched.startTime && (
                          <InputError errorTitle={errors.startTime} />
                        )}
                      </Stack>
                    </LocalizationProvider>
                  </div>

                  {/* End Time */}
                  <div style={{ zIndex: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Stack spacing={3}>
                        <MobileTimePicker
                          label="End Time"
                          value={
                            values.startTime
                              ? dayjs(values.endTime, "HH:mm:ss")
                              : dayjs("16:00:00", "HH:mm:ss")
                          }
                          onChange={(newValue) => {
                            const formattedTime =
                              dayjs(newValue).format("HH:mm:ss");
                            setFieldValue("endTime", formattedTime);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                padding: "0px",
                                minHeight: "unset",
                                "& .MuiOutlinedInput-root": {
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#F15B29",
                                    borderWidth: "1px",
                                  },
                                },
                              },
                            },
                          }}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        {errors.endTime && touched.endTime && (
                          <InputError errorTitle={errors.endTime} />
                        )}
                      </Stack>
                    </LocalizationProvider>
                  </div>
                </div>
              </div>
              {/* Time To Avoid */}
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h3 className="text-xs font-normal mb-4">
                  Time to Avoid (Up to 3 time range)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {values.avoid_time.map((range, index) => (
                    <>
                      <div key={index}>
                        {/* Start Time */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Stack spacing={3}>
                            <MobileTimePicker
                              label="Start Time"
                              value={
                                dayjs(range.startTime, "HH:mm:ss").isValid()
                                  ? dayjs(range.startTime, "HH:mm:ss")
                                  : null
                              }
                              onChange={(newValue) => {
                                const formattedTime =
                                  dayjs(newValue).format("HH:mm:ss");
                                setFieldValue(
                                  `avoid_time[${index}].startTime`,
                                  formattedTime
                                );
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: {
                                    padding: "0px",
                                    minHeight: "unset",
                                    "& .MuiOutlinedInput-root": {
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#F15B29",
                                        borderWidth: "1px",
                                      },
                                    },
                                  },
                                },
                              }}
                              disabled={isEditMode == "edit" && !isEditing}
                            />
                            {errors?.avoid_time?.[index]?.startTime &&
                              touched?.avoid_time?.[index]?.startTime && (
                                <InputError
                                  errorTitle={
                                    errors?.avoid_time[index]?.startTime
                                  }
                                />
                              )}
                          </Stack>
                        </LocalizationProvider>
                      </div>
                      <div>
                        {/* End Time */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Stack spacing={3}>
                            <MobileTimePicker
                              label="End Time"
                              value={
                                dayjs(range.endTime, "HH:mm:ss").isValid()
                                  ? dayjs(range.endTime, "HH:mm:ss")
                                  : null
                              }
                              onChange={(newValue) => {
                                const formattedTime =
                                  dayjs(newValue).format("HH:mm:ss");
                                setFieldValue(
                                  `avoid_time[${index}].endTime`,
                                  formattedTime
                                );
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: {
                                    padding: "0px",
                                    minHeight: "unset",
                                    "& .MuiOutlinedInput-root": {
                                      "&.Mui-focused fieldset": {
                                        borderColor: "#F15B29",
                                        borderWidth: "1px",
                                      },
                                    },
                                  },
                                },
                              }}
                              disabled={isEditMode == "edit" && !isEditing}
                            />
                            {errors?.avoid_time?.[index]?.endTime &&
                              touched?.avoid_time?.[index]?.endTime && (
                                <InputError
                                  errorTitle={
                                    errors?.avoid_time[index]?.endTime
                                  }
                                />
                              )}
                          </Stack>
                        </LocalizationProvider>
                      </div>
                    </>
                  ))}
                </div>
                <div className="flex items-center justify-center my-4">
                  <div className="flex-grow border-t border-bgBtnGray"></div>
                  <button
                    type="button"
                    onClick={(e) => addTimeRange(e, setFieldValue, values)}
                    className={`bg-bgBtnGray w-32 h-10 rounded-4xl flex justify-center items-center shadow-lg mx-4 px-4 py-2 ${
                      values?.avoid_time?.length >= 3 ||
                      (isEditMode == "edit" && !isEditing)
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={
                      values?.avoid_time?.length >= 3 ||
                      (isEditMode == "edit" && !isEditing)
                    }
                  >
                    <Plus className="fill-black mr-2" />{" "}
                    <p className="font-Proxima text-sm">ADD MORE</p>
                  </button>

                  <div className="flex-grow border-t border-bgBtnGray"></div>
                </div>
              </div>
              {/* staff */}
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h3 className="text-xs font-normal mb-4">Outlet's Staff</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <SelectWithLabel
                      idFromName="manager"
                      placeholder="Outlet's Manager"
                      options={managerOptions}
                      value={
                        values?.manager?.value
                          ? values?.manager?.value
                          : values?.manager
                      }
                      disabled={isEditMode == "edit" && !isEditing}
                      endAdornment={
                        values?.manager ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`manager`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      setFieldValue={setFieldValue}
                      onChange={(event) => {
                        setFieldValue("manager", event.target.value);
                      }}
                    />
                  </div>
                  <div>
                    <SelectWithLabel
                      idFromName="staff"
                      placeholder="Outlet's Staff"
                      options={staffOptions}
                      value={
                        values?.staff?.value
                          ? values?.staff?.value
                          : values?.staff
                      }
                      disabled={isEditMode == "edit" && !isEditing}
                      endAdornment={
                        values?.staff ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`staff`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      setFieldValue={setFieldValue}
                      onChange={(event) => {
                        setFieldValue("staff", event.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Equipments */}
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h3 className="text-xs font-normal mb-4">Equipments</h3>
                {values.equipments.map((equipment, index) => (
                  <div key={index} className="grid grid-cols-1 gap-4 mb-4">
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black  border hover:border-themeBtn"
                      }  ${
                        errors.equipments?.[index]?.on_site_name &&
                        touched.equipments?.[index]?.on_site_name
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName={`equipments[${index}].on_site_name`}
                      label="On-Site Name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={equipment.on_site_name}
                      disabled={isEditMode == "edit" && !isEditing}
                      errors={
                        errors.equipments?.[index]?.on_site_name &&
                        touched.equipments?.[index]?.on_site_name
                      }
                      errorsText={errors.equipments?.[index]?.on_site_name}
                    />
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
                    >
                      <div>
                        <SelectWithLabel
                          idFromName={`equipments[${index}].product_id`}
                          placeholder="Equipment"
                          options={productOptions}
                          value={equipment.product_id || ""}
                          setFieldValue={setFieldValue}
                          errors={
                            errors.equipments &&
                            errors.equipments[index]?.product_id
                          }
                          disabled={isEditMode == "edit" && !isEditing}
                          endAdornment={
                            equipment.product_id ? (
                              <IconButton
                                sx={{
                                  position: "relative",
                                  right: "10px",
                                }}
                                onClick={() =>
                                  setFieldValue(
                                    `equipments[${index}].product_id`,
                                    ""
                                  )
                                }
                              >
                                <ClearIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            ) : null
                          }
                          touched={
                            touched.equipments &&
                            touched.equipments[index]?.product_id
                          }
                          onChange={(event) => {
                            const selectedValue = event.target.value;
                            handleChange({
                              target: {
                                name: `equipments[${index}].product_id`,
                                value: selectedValue,
                              },
                            });
                            setFieldValue(
                              `equipments[${index}].product_id`,
                              selectedValue
                            );
                          }}
                        />
                      </div>
                      <div>
                        <DatePicker
                          id={`equipments[${index}].purchase_date`}
                          value={
                            values.equipments[index].purchase_date
                              ? moment(values.equipments[index].purchase_date)
                              : null
                          }
                          onChange={(date) =>
                            setFieldValue(
                              `equipments[${index}].purchase_date`,
                              date ? date.toDate() : null
                            )
                          }
                          disabled={isEditMode === "edit" && !isEditing}
                          format="DD/MM/YYYY"
                          icon={null}
                        />
                      </div>
                    </div>
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 mr-2 accent-themeBtn"
                        checked={equipment.warranty}
                        disabled={isEditMode == "edit" && !isEditing}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: `equipments[${index}].warranty`,
                              value: e.target.checked,
                            },
                          })
                        }
                      />
                      <span
                        className={`text-base font-normal text-primaryBlack ${
                          isEditMode == "edit" && !isEditing
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        Include Warranty
                      </span>
                    </label>
                    {equipment.warranty && (
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <SelectWithLabel
                            idFromName="warrantyPeriod"
                            placeholder="Warranty Period (Months)"
                            options={[
                              { value: "3", label: "3 Months" },
                              { value: "6", label: "6 Months" },
                              { value: "12", label: "12 Months" },
                              { value: "24", label: "24 Months" },
                            ]}
                            value={equipment.warrantyPeriod || ""}
                            setFieldValue={setFieldValue}
                            errors={errors.equipments?.[index]?.warrantyPeriod}
                            touched={
                              touched.equipments?.[index]?.warrantyPeriod
                            }
                            disabled={isEditMode == "edit" && !isEditing}
                            endAdornment={
                              equipment.warrantyPeriod ? (
                                <IconButton
                                  sx={{
                                    position: "relative",
                                    right: "10px",
                                  }}
                                  onClick={() =>
                                    setFieldValue(
                                      `equipments[${index}].warrantyPeriod`,
                                      ""
                                    )
                                  }
                                >
                                  <ClearIcon sx={{ fontSize: "16px" }} />
                                </IconButton>
                              ) : null
                            }
                            onChange={(event) => {
                              const selectedValue = event.target.value;
                              handleChange({
                                target: {
                                  name: `equipments[${index}].warrantyPeriod`,
                                  value: selectedValue,
                                },
                              });
                              setFieldValue(
                                `equipments[${index}].warrantyPeriod`,
                                selectedValue
                              );
                            }}
                          />
                          {errors.equipments?.[index]?.warrantyPeriod &&
                            touched.equipments?.[index]?.warrantyPeriod && (
                              <InputError
                                errorTitle={
                                  errors.equipments[index].warrantyPeriod
                                }
                              />
                            )}
                        </div>
                        <InputWithLabel
                          className={`${
                            isEditMode === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black  border hover:border-themeBtn"
                          }  ${
                            errors.equipments?.[index]?.serialNumber &&
                            touched.equipments?.[index]?.serialNumber
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out`}
                          idFromName={`equipments[${index}].serialNumber`}
                          label="Serial Number"
                          type="text"
                          name={`equipments[${index}].serialNumber`}
                          value={equipment.serialNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={
                            errors.equipments?.[index]?.serialNumber &&
                            touched.equipments?.[index]?.serialNumber
                          }
                          errorsText={errors.equipments?.[index]?.serialNumber}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-center my-4">
                  <div className="flex-grow border-t border-bgBtnGray"></div>
                  <button
                    type="button"
                    onClick={() => {
                      const newEquipment = {
                        product_id: "",
                        on_site_name: "",
                        purchase_date: dayjs(),
                        warranty: false,
                        warrantyPeriod: "",
                        serialNumber: "",
                      };
                      handleChange({
                        target: {
                          name: "equipments",
                          value: [...values.equipments, newEquipment],
                        },
                      });
                    }}
                    disabled={isEditMode == "edit" && !isEditing}
                    className={`bg-bgBtnGray w-32 h-10 rounded-4xl flex justify-center items-center shadow-lg mx-4 px-4 py-2 ${
                      isEditMode == "edit" && !isEditing
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <Plus className="fill-black mr-2" />
                    <p className="font-Proxima text-sm">ADD MORE</p>
                  </button>
                  <div className="flex-grow border-t border-bgBtnGray"></div>
                </div>
              </div>
              {/* remark */}
              <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                <h3 className="text-xs font-normal mb-4">Remarks (Optional)</h3>
                <InputWithLabel
                  idFromName="remarks"
                  label="Description"
                  type="textarea"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.remarks}
                  errors={errors.remarks && touched.remarks}
                  errorsText={errors.remarks}
                  disabled={isEditMode == "edit" && !isEditing}
                  rows={3}
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black  border hover:border-themeBtn"
                  }  ${
                    errors.remarks && touched.remarks
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                />
              </div>
              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value="Cancel"
                  onClick={() => navigate(-1)}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton type="submit" value="Save" />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <DefaultButton type="submit" value="Save" />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleSubmit(formValues)}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default AddNewOutlet;
